/**
 * LegacyRjqFacade
 *
 * Full legacy-to-React facade vocabulary.
 *
 * LegacyUiCommandRouter is intentionally narrower: it resolves meaningful UI
 * mutations. This file describes the complete $rjq language boundary so every
 * legacy-facing call can be normalized before React/template/store code sees
 * the effect.
 */

import type { LegacyUiTarget } from './CommandRouter';

export type LegacyRjqOpKind =
  | 'factory'
  | 'mutation'
  | 'event'
  | 'traversal'
  | 'read'
  | 'browser'
  | 'plugin'
  | 'static'
  | 'internal';

export type LegacyRjqMutationOp =
  | 'show'
  | 'hide'
  | 'toggle'
  | 'fadeTo'
  | 'fadeIn'
  | 'fadeOut'
  | 'slideToggle'
  | 'addClass'
  | 'removeClass'
  | 'toggleClass'
  | 'text'
  | 'html'
  | 'val'
  | 'attr'
  | 'prop'
  | 'removeAttr'
  | 'css'
  | 'append'
  | 'prepend'
  | 'before'
  | 'after'
  | 'insertAfter'
  | 'insertBefore'
  | 'empty'
  | 'remove'
  | 'replaceWith';

export type LegacyRjqEventOp =
  | 'on'
  | 'click'
  | 'mouseenter'
  | 'mouseleave'
  | 'hover'
  | 'keyup'
  | 'keydown'
  | 'keypress'
  | 'mousedown'
  | 'mouseup'
  | 'bind'
  | 'unbind'
  | 'trigger'
  | 'change'
  | 'blur'
  | 'focusin'
  | 'focusout'
  | 'submit'
  | 'resize'
  | 'ready';

export type LegacyRjqTraversalOp =
  | 'parent'
  | 'next'
  | 'prev'
  | 'children'
  | 'find'
  | 'first'
  | 'eq'
  | 'filter'
  | 'not'
  | 'add'
  | 'each'
  | 'clone';

export type LegacyRjqReadOp =
  | 'hasClass'
  | 'is'
  | 'get'
  | 'getDOMNode'
  | 'width'
  | 'height'
  | 'css:get'
  | 'attr:get'
  | 'text:get'
  | 'html:get'
  | 'val:get'
  | 'scrollTop:get';

export type LegacyRjqBrowserOp =
  | 'focus'
  | 'scrollTop'
  | 'disableTextSelect';

export type LegacyRjqPluginOp =
  | 'validate'
  | 'ajaxSubmit'
  | 'tinymce'
  | 'dynDateTime'
  | 'ColorPicker'
  | 'simpleColorPicker'
  | 'wookmark';

export type LegacyRjqStaticOp =
  | '$rjq'
  | 'extend'
  | 'trim'
  | 'parseJSON'
  | 'static.each'
  | 'static.isFunction'
  | 'static.isArray'
  | 'static.ajaxSetup'
  | 'static.ajax'
  | 'static.get'
  | 'static.post'
  | 'static.format'
  | 'static.validator:get'
  | 'static.validator:set';

export type LegacyRjqOpName =
  | LegacyRjqMutationOp
  | LegacyRjqEventOp
  | LegacyRjqTraversalOp
  | LegacyRjqReadOp
  | LegacyRjqBrowserOp
  | LegacyRjqPluginOp
  | LegacyRjqStaticOp
  | string;

export interface LegacyRjqNormalizedOp {
  kind: LegacyRjqOpKind;
  legacyOp: LegacyRjqOpName;
  target?: LegacyUiTarget;
  targetCount?: number;
  args?: unknown[];
  resultKind?: 'self' | 'collection' | 'group' | 'value' | 'void' | 'delegated' | 'unknown';
  source?: 'ReactJQuery' | 'ReactJQueryGroup' | 'ChildReactJQuery' | '$rjq' | 'static' | 'legacy-bridge';
  notes?: string;
}

export type LegacyRjqFacadeSink = (op: LegacyRjqNormalizedOp) => void;

export function classifyLegacyRjqOp(name: string, args: unknown[] = []): LegacyRjqOpKind {
  if (
    name === 'show' || name === 'hide' || name === 'toggle' || name === 'fadeTo' ||
    name === 'fadeIn' || name === 'fadeOut' || name === 'slideToggle' ||
    name === 'addClass' || name === 'removeClass' || name === 'toggleClass' ||
    name === 'append' || name === 'prepend' || name === 'before' || name === 'after' ||
    name === 'insertAfter' || name === 'insertBefore' || name === 'empty' ||
    name === 'remove' || name === 'replaceWith' || name === 'removeAttr'
  ) return 'mutation';

  if (name === 'text' || name === 'html' || name === 'val') {
    return args.length === 0 ? 'read' : 'mutation';
  }

  if (name === 'attr' || name === 'css') {
    return args.length <= 1 && typeof args[0] === 'string' ? 'read' : 'mutation';
  }

  if (name === 'prop') {
    return args.length <= 1 ? 'read' : 'mutation';
  }

  if (
    name === 'on' || name === 'click' || name === 'mouseenter' || name === 'mouseleave' ||
    name === 'hover' || name === 'keyup' || name === 'keydown' || name === 'keypress' ||
    name === 'mousedown' || name === 'mouseup' || name === 'bind' || name === 'unbind' ||
    name === 'trigger' || name === 'change' || name === 'focusin' || name === 'focusout' ||
    name === 'submit' || name === 'resize' || name === 'ready'
  ) return 'event';

  if (name === 'blur') return args.length === 0 ? 'browser' : 'event';

  if (
    name === 'parent' || name === 'next' || name === 'prev' || name === 'children' ||
    name === 'find' || name === 'first' || name === 'eq' || name === 'filter' ||
    name === 'not' || name === 'add' || name === 'each' || name === 'clone'
  ) return 'traversal';

  if (
    name === 'hasClass' || name === 'is' || name === 'get' || name === 'getDOMNode' ||
    name === 'width' || name === 'height'
  ) return args.length === 0 || name !== 'width' && name !== 'height' ? 'read' : 'mutation';

  if (name === 'scrollTop') return args.length === 0 ? 'read' : 'browser';

  if (name === 'focus' || name === 'disableTextSelect') return 'browser';

  if (
    name === 'validate' || name === 'ajaxSubmit' || name === 'tinymce' ||
    name === 'dynDateTime' || name === 'ColorPicker' || name === 'simpleColorPicker' ||
    name === 'wookmark'
  ) return 'plugin';

  if (name.startsWith('static.') || name === '$rjq') return 'static';

  return 'internal';
}

export function normalizeLegacyRjqOp(input: {
  legacyOp: string;
  target?: LegacyUiTarget;
  targetCount?: number;
  args?: unknown[];
  resultKind?: LegacyRjqNormalizedOp['resultKind'];
  source?: LegacyRjqNormalizedOp['source'];
  notes?: string;
}): LegacyRjqNormalizedOp {
  const args = input.args || [];
  return {
    kind: classifyLegacyRjqOp(input.legacyOp, args),
    legacyOp: input.legacyOp,
    target: input.target,
    targetCount: input.targetCount,
    args,
    resultKind: input.resultKind,
    source: input.source,
    notes: input.notes,
  };
}
